import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BehaviorSubject,
  combineLatest,
  combineLatestWith,
  filter,
  Observable,
} from 'rxjs';
import {
  distinctUntilChanged,
  map,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { of } from 'rxjs/internal/observable/of';

import {
  CatalogClient,
  CatalogListItemResponse as CatalogItem,
  GetCatalogCategoriesResponse as Category,
  GetCatalogCategoryDetailedInfoResponse as DetailedInfo,
  GetCatalogCategoryAttributesResponse as Attribute,
} from '@medindex-webapi';
import { StorageService } from '@shared/core/services/storage.service';
import { DatasourceService } from '@shared/core/services/datasource.service';
import { typeNomenclature } from '@shared/utils/getTypeNomenclature';
import { TypeNomenclature } from '@shared/enums/type-nomenclature';
import { QueryParamsUrl } from '@shared/classes/query-params-url';
import { HelixRoutes } from '@shared/enums/routes';
import { RegionsService } from '@shared/services/regions.service';
import { NavigationMenuService } from '@shared/services/navigation-menu.service';
import { PageSize } from '../const/catalog-pagination-sizes';
import { StateCatalogList } from '../interfaces/state-catalog-list.interfaces';

@Injectable()
export class CatalogRepositoryService
  extends QueryParamsUrl
  implements OnDestroy
{
  constructor(
    private catalogClient: CatalogClient,
    private dataSource: DatasourceService,
    private storage: StorageService,
    private navigationMenuService: NavigationMenuService,
    public router: Router,
    public route: ActivatedRoute,
    private regions: RegionsService
  ) {
    super(router, route);

    // Пока не отключаем возможность записи через старый флоу для пилотных городов
    // this.setRedirectToPractitionerAppointment();
    this.handlingCategoryFromUrl();
    this.setState();

    this.getCategoryDetailedInfo();
    this.getAttributes();
    this.getCatalogList();
  }

  private state$ = new BehaviorSubject<StateCatalogList | null>(null);
  private cityId$ = this.storage.cityId$.pipe(
    filter((cityId): cityId is number => !!cityId),
    this.regions.getWaitForAliasAppliedToUrlPipeOperator()
  );

  categories$ = this.navigationMenuService.catalogCategories$;
  selectedCategory$ = new BehaviorSubject<Category | null>(null);

  attributes$ = new BehaviorSubject<Attribute[]>([]);
  selectedAttributes$ = this.state$.pipe(
    filter((state): state is StateCatalogList => !!state),
    map(({ attributes }) => attributes),
    distinctUntilChanged((a, b) => this.areSameArrays(a, b))
  );

  currentPage$ = this.state$.pipe(
    filter((state): state is StateCatalogList => !!state),
    map(({ page }) => page),
    distinctUntilChanged()
  );

  detailedInfo$ = new BehaviorSubject<string | null>(null);
  catalogItems$ = new BehaviorSubject<CatalogItem[] | null>(null);

  metaData$ = this.selectedCategory$.pipe(
    switchMap((category) => {
      // мета данные для категории 'врачебные услуги'
      if (
        category?.id === this.navigationMenuService.doctorServicesCategoryId
      ) {
        const title$ = this.regions.getTitleWithCity('Запись к врачу');
        const metaTitle$ = this.regions.getTitleWithCity(
          'Записаться на приём к врачу в Хеликс'
        );
        const description$ = title$.pipe(
          map(
            (title) =>
              title +
              ' на сайте, по телефону или в Центре. Опытные специалисты рядом. Более 150 врачебных услуг.'
          )
        );

        return combineLatest([title$, metaTitle$, description$]);
      }

      if (!category?.description) return of(['', '', '']);

      const title$ = this.regions.getTitleWithCity(category.description);
      const metaTitle$ = title$.pipe(
        map((title) => `Сдать ${title}, цены, медицинский центр Хеликс`)
      );
      const description$ = this.regions
        .getTitleWithCity('сеть лабораторий Хеликс')
        .pipe(
          map(
            (description) =>
              `\u2705 Сдать лабораторные медицинские ${category.description}: ${description}, прием с пн по вс, быстро и без очереди, результаты выполнения от 1 дня.`
          )
        );

      return combineLatest([title$, metaTitle$, description$]);
    })
  );

  title$ = this.metaData$.pipe(
    map(({ 0: title$ }) => title$),
    distinctUntilChanged()
  );
  metaTitle$ = this.metaData$.pipe(
    map(({ 1: metaTitle$ }) => metaTitle$),
    distinctUntilChanged()
  );
  description$ = this.metaData$.pipe(
    map(({ 2: description$ }) => description$),
    distinctUntilChanged()
  );

  breadcrumbs$ = this.title$.pipe(
    map((title) => [
      {
        text: 'Главная',
        href: '/',
      },
      {
        text: title,
        href: null,
      },
    ])
  );

  complexes$: Observable<CatalogItem[]> = this.catalogItems$.pipe(
    filter(
      (catalogItems: CatalogItem[] | null): catalogItems is CatalogItem[] =>
        !!catalogItems
    ),
    map((items: CatalogItem[]) =>
      items.filter(
        (item: CatalogItem): boolean =>
          typeNomenclature(item.hxid || '', item.isComplex) ===
          TypeNomenclature.Complex
      )
    )
  );

  analyses$: Observable<CatalogItem[]> = this.catalogItems$.pipe(
    filter(
      (catalogItems: CatalogItem[] | null): catalogItems is CatalogItem[] =>
        !!catalogItems
    ),
    map((items: CatalogItem[]) =>
      items.filter(
        (item: CatalogItem): boolean =>
          typeNomenclature(item.hxid || '', item.isComplex) ===
          TypeNomenclature.Analysis
      )
    )
  );

  services$: Observable<CatalogItem[]> = this.catalogItems$.pipe(
    filter(
      (catalogItems: CatalogItem[] | null): catalogItems is CatalogItem[] =>
        !!catalogItems
    ),
    map((items: CatalogItem[]) =>
      items.filter(
        (item: CatalogItem): boolean =>
          typeNomenclature(item.hxid || '', item.isComplex) ===
          TypeNomenclature.Service
      )
    )
  );

  pendingLoad$ = new BehaviorSubject<boolean>(true);
  count$ = new BehaviorSubject<number | null>(null);

  private setRedirectToPractitionerAppointment(): void {
    this.selectedCategory$
      .pipe(
        filter(
          (category) =>
            category?.id === this.navigationMenuService.doctorServicesCategoryId
        ),
        switchMap(() => this.navigationMenuService.isAppointmentAvailable$),
        distinctUntilChanged(),
        tap((isAppointmentAvailable) => {
          if (isAppointmentAvailable) {
            this.router.navigate([HelixRoutes.PractitionerAppointment]);
          }
        }),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe();
  }

  private handlingCategoryFromUrl(): void {
    this.route.params
      .pipe(
        combineLatestWith(this.categories$),
        filter(([, categories]) => !!categories && !!categories.length),
        tap(([{ category }, categories]): void => {
          const currentCategory = this.getCategory(
            category,
            categories as Category[]
          );

          if (currentCategory) {
            this.selectedCategory$.next(currentCategory);
          } else {
            this.navigateToCatalog();
          }
        }),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe();
  }

  private setState(): void {
    this.selectedCategory$
      .pipe(
        filter((category) => !!category),
        combineLatestWith(this.cityId$, this.queryParams$),
        tap(([category, cityId, { page, attributes }]): void => {
          const state = {
            cityId,
            category,
            attributes: this.convertAttributes(attributes),
            page: page ? Number(page) : 1,
          };

          this.state$.next(state);
        }),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe();
  }

  private getCategoryDetailedInfo(): void {
    this.selectedCategory$
      .pipe(
        filter((category): category is Category => !!category),
        this.regions.getWaitForAliasAppliedToUrlPipeOperator(),
        switchMap(({ id }) =>
          this.catalogClient.getCatalogCategoryDetailedInfo(id)
        ),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe({
        next: ({ detailedInfo }: DetailedInfo) =>
          this.detailedInfo$.next(detailedInfo),
        error: (error) => this.dataSource.errorHandler(error),
      });
  }

  private getAttributes(): void {
    this.selectedCategory$
      .pipe(
        filter((category): category is Category => !!category),
        distinctUntilChanged((prev, current) => prev.id === current.id),
        combineLatestWith(this.cityId$),
        tap(() => this.attributes$.next([])),
        switchMap(([category, cityId]) =>
          this.catalogClient.getCatalogCategoryAttributes(
            cityId,
            category.id || -1
          )
        ),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe({
        next: (attributes: Attribute[]) => this.attributes$.next(attributes),
        error: (error) => this.dataSource.errorHandler(error),
      });
  }

  private getCatalogList(): void {
    this.state$
      .pipe(
        filter((state): state is StateCatalogList => !!state),
        this.regions.getWaitForAliasAppliedToUrlPipeOperator(),
        tap((): void => {
          this.setLoading(true);
          this.catalogItems$.next([]);
        }),
        switchMap(({ category, cityId, attributes, page }) =>
          this.catalogClient.getCatalogListItemsV2(
            cityId,
            category?.id || -1,
            attributes,
            PageSize,
            (page - 1) * PageSize
          )
        ),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe({
        next: ({ total, catalogItems }): void => {
          this.count$.next(total);
          this.catalogItems$.next(catalogItems);
          this.setLoading(false);
        },
        error: (error): void => {
          this.dataSource.errorHandler(error);
          this.count$.next(0);
        },
      });
  }

  private getCategory(
    urlPart: string | null,
    categories: Category[]
  ): Category | null {
    return urlPart
      ? categories.find((item: Category) => item.urlPart === urlPart) ?? null
      : categories[0] ?? null;
  }

  private convertAttributes(attributes: string | undefined): number[] {
    return attributes?.split(',').map((attribute) => Number(attribute)) || [];
  }

  private setLoading(value: boolean): void {
    this.pendingLoad$.next(value);
  }

  private areSameArrays(a: number[], b: number[]): boolean {
    return a.toString() === b.toString();
  }

  private navigateToCatalog(): void {
    this.router.navigate([HelixRoutes.Catalog]);
  }
}
